import { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Handshake, CalendarClock, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { PageHeader, StatusBadge, Empty } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime, toLocalInput } from "@/lib/api";

function NewMeetingDialog({ open, onOpenChange, onDone, partners }) {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const ref = partners.find((p) => p.id === user.reference_partner_id);
  const [people, setPeople] = useState([]);
  const [f, setF] = useState({ target: ref ? "reference" : isAdmin ? "employee" : "all", employee_id: "", admin_id: "", topic: "", description: "", preferred_at: "" });
  useEffect(() => { api.get("/users/directory").then((r) => setPeople(r.data.filter((p) => p.id !== user.id))); }, [user.id]);
  const admins = useMemo(() => people.filter((p) => p.role === "admin"), [people]);
  const employees = useMemo(() => people.filter((p) => p.role !== "admin"), [people]);
  const submit = async (e) => {
    e.preventDefault();
    if (f.target === "employee" && !f.employee_id) return toast.error("Seleziona un dipendente");
    if (f.target === "admin" && !f.admin_id) return toast.error("Seleziona un amministratore");
    try {
      await api.post("/meetings", { ...f, preferred_at: f.preferred_at ? new Date(f.preferred_at).toISOString() : null });
      toast.success("Richiesta di incontro inviata");
      onOpenChange(false);
      onDone();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="meeting-dialog">
        <DialogHeader>
          <DialogTitle>Richiedi un incontro</DialogTitle>
          <DialogDescription>{isAdmin ? "A un dipendente, a un amministratore, al tuo referente o a tutti i soci." : "A un amministratore, al tuo referente o a tutti i soci (incontro di gruppo)."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <RadioGroup value={f.target} onValueChange={(v) => setF({ ...f, target: v })} className="grid gap-2 sm:grid-cols-2">
            {isAdmin && (
              <label className="flex cursor-pointer items-start gap-2.5 rounded-md border p-3 text-sm">
                <RadioGroupItem value="employee" className="mt-0.5" data-testid="meeting-target-employee" />
                <span><span className="block font-medium">Un dipendente</span><span className="text-xs text-muted-foreground">Incontro individuale</span></span>
              </label>
            )}
            <label className={`flex cursor-pointer items-start gap-2.5 rounded-md border p-3 text-sm ${!admins.length ? "opacity-50" : ""}`}>
              <RadioGroupItem value="admin" disabled={!admins.length} className="mt-0.5" data-testid="meeting-target-admin" />
              <span><span className="block font-medium">Un amministratore</span><span className="text-xs text-muted-foreground">{admins.length} amministratori</span></span>
            </label>
            <label className={`flex cursor-pointer items-start gap-2.5 rounded-md border p-3 text-sm ${!ref ? "opacity-50" : ""}`}>
              <RadioGroupItem value="reference" disabled={!ref} className="mt-0.5" data-testid="meeting-target-reference" />
              <span><span className="block font-medium">Il mio referente</span><span className="text-xs text-muted-foreground">{ref ? ref.name : <>Nessuno – <Link to="/profilo" className="underline">imposta nel Profilo</Link></>}</span></span>
            </label>
            <label className="flex cursor-pointer items-start gap-2.5 rounded-md border p-3 text-sm">
              <RadioGroupItem value="all" className="mt-0.5" data-testid="meeting-target-all" />
              <span><span className="block font-medium">Tutti i soci</span><span className="text-xs text-muted-foreground">{partners.filter((p) => p.id !== user.id).length} soci</span></span>
            </label>
          </RadioGroup>
          {f.target === "employee" && (
            <div className="space-y-1.5">
              <Label>Dipendente</Label>
              <Select value={f.employee_id} onValueChange={(v) => setF({ ...f, employee_id: v })}>
                <SelectTrigger data-testid="meeting-employee-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{employees.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}{p.department ? ` · ${p.department}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          {f.target === "admin" && (
            <div className="space-y-1.5">
              <Label>Amministratore</Label>
              <Select value={f.admin_id} onValueChange={(v) => setF({ ...f, admin_id: v })}>
                <SelectTrigger data-testid="meeting-admin-select"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                <SelectContent>{admins.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5"><Label>Argomento</Label><Input value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} required minLength={3} data-testid="meeting-topic-input" /></div>
          <div className="space-y-1.5"><Label>Dettagli</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} data-testid="meeting-description-input" /></div>
          <div className="space-y-1.5"><Label>Data e ora preferite (opzionale)</Label><Input type="datetime-local" step={900} value={f.preferred_at} onChange={(e) => setF({ ...f, preferred_at: e.target.value })} data-testid="meeting-preferred-input" /></div>
          <DialogFooter><Button type="submit" data-testid="meeting-submit-button">Invia richiesta</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RespondDialog({ meeting, action, onClose, onDone }) {
  const [when, setWhen] = useState(toLocalInput(meeting?.preferred_at));
  const [note, setNote] = useState("");
  const submit = async () => {
    try {
      await api.post(`/meetings/${meeting.id}/respond`, { action, note, scheduled_at: action === "accept" && when ? new Date(when).toISOString() : null });
      toast.success(action === "accept" ? "Incontro accettato" : "Incontro rifiutato");
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="respond-dialog">
        <DialogHeader>
          <DialogTitle>{action === "accept" ? "Accetta incontro" : "Rifiuta incontro"}</DialogTitle>
          <DialogDescription>{meeting.requester_name} · {meeting.topic}</DialogDescription>
        </DialogHeader>
        {action === "accept" && (
          <div className="space-y-1.5"><Label>Data e ora proposte</Label><Input type="datetime-local" step={900} value={when} onChange={(e) => setWhen(e.target.value)} required data-testid="respond-datetime-input" /></div>
        )}
        <div className="space-y-1.5"><Label>Nota</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="respond-note-input" /></div>
        <DialogFooter><Button onClick={submit} disabled={action === "accept" && !when} data-testid="respond-confirm-button">Conferma</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MeetingCard({ m, me, onRespond, onCancel }) {
  const mine = m.requester_id === me.id;
  const myResp = m.recipients.find((r) => r.id === me.id);
  const canRespond = myResp && !myResp.response && m.status !== "cancelled";
  return (
    <div className="lift fade-up rounded-lg border bg-card p-5" data-testid={`meeting-card-${m.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <StatusBadge status={m.status} testId={`meeting-status-${m.id}`} />
        <span className="text-xs text-muted-foreground">{{ all: "Incontro di gruppo", reference: "Referente", employee: "Richiesto dal titolare", admin: "Ad un amministratore" }[m.target]} · {fmtDateTime(m.created_at)}</span>
      </div>
      <h3 className="mt-2 text-base font-semibold">{m.topic}</h3>
      {m.description && <p className="mt-1 text-sm text-muted-foreground">{m.description}</p>}
      <p className="mt-2 text-xs text-muted-foreground">Richiesto da <span className="font-medium text-foreground">{m.requester_name}</span>{m.preferred_at && ` · preferenza ${fmtDateTime(m.preferred_at)}`}</p>
      {m.scheduled_at && (
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1 text-sm font-medium text-primary" data-testid={`meeting-scheduled-${m.id}`}>
          <CalendarClock className="h-4 w-4" />{fmtDateTime(m.scheduled_at)}
        </p>
      )}
      <ul className="mt-4 space-y-1.5 border-t pt-3">
        {m.recipients.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-medium">{r.name}</span>
            <span className="text-xs text-muted-foreground">
              {!r.response ? "in attesa di risposta" : r.response.status === "accept" ? `ha accettato per ${fmtDateTime(r.response.scheduled_at)}` : "ha rifiutato"}
              {r.response?.note && ` – “${r.response.note}”`}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-4 flex gap-2">
        {canRespond && (
          <>
            <Button size="sm" onClick={() => onRespond(m, "accept")} data-testid={`meeting-accept-${m.id}`}><Check className="mr-1 h-4 w-4" />Accetta</Button>
            <Button size="sm" variant="outline" onClick={() => onRespond(m, "decline")} data-testid={`meeting-decline-${m.id}`}><X className="mr-1 h-4 w-4" />Rifiuta</Button>
          </>
        )}
        {mine && ["pending", "accepted"].includes(m.status) && (
          <Button size="sm" variant="outline" onClick={() => onCancel(m)} data-testid={`meeting-cancel-${m.id}`}>Annulla richiesta</Button>
        )}
      </div>
    </div>
  );
}

export default function Incontri() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [tab, setTab] = useState(null);
  const [open, setOpen] = useState(false);
  const [resp, setResp] = useState(null);
  const load = useCallback(() => api.get("/meetings").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); api.get("/partners").then((r) => setPartners(r.data)); }, [load]);

  const cancel = async (m) => {
    try { await api.post(`/meetings/${m.id}/cancel`); toast.success("Richiesta annullata"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const received = items.some((m) => m.recipient_ids.includes(user.id));
  const current = tab || (received ? "received" : "mine");
  const shown = items.filter((m) =>
    current === "mine" ? m.requester_id === user.id : current === "received" ? m.recipient_ids.includes(user.id) : true);
  const pendingReceived = items.filter((m) => m.recipient_ids.includes(user.id) && m.status !== "cancelled" && !m.responses[user.id]).length;

  return (
    <div data-testid="meetings-page">
      <PageHeader title="Incontri" subtitle="Richieste di incontro con soci, titolare e dipendenti, con conferma di data e ora e promemoria automatici."
        actions={<Button onClick={() => setOpen(true)} data-testid="new-meeting-button"><Plus className="mr-1.5 h-4 w-4" />Richiedi incontro</Button>} />
      <Tabs value={current} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="mine" data-testid="meetings-tab-mine">Le mie richieste</TabsTrigger>
          {(user.is_partner || user.role === "admin" || received) && <TabsTrigger value="received" data-testid="meetings-tab-received">Ricevute{pendingReceived > 0 && ` (${pendingReceived})`}</TabsTrigger>}
          {user.role === "admin" && <TabsTrigger value="all" data-testid="meetings-tab-all">Tutte</TabsTrigger>}
        </TabsList>
      </Tabs>
      {shown.length === 0 ? (
        <Empty icon={Handshake} text="Nessun incontro da mostrare." testId="meetings-empty" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {shown.map((m) => <MeetingCard key={m.id} m={m} me={user} onRespond={(mm, a) => setResp({ m: mm, a })} onCancel={cancel} />)}
        </div>
      )}
      {open && <NewMeetingDialog open={open} onOpenChange={setOpen} onDone={load} partners={partners} />}
      {resp && <RespondDialog meeting={resp.m} action={resp.a} onClose={() => setResp(null)} onDone={load} />}
    </div>
  );
}
